"use client";
import { cn } from "@/lib/cn";

/**
 * Segmented control / tab strip.
 *
 * Two distinct surfaces collapsed into one component, because operator
 * pages used both interchangeably:
 *
 *   variant="segmented" (default) — rounded button group inside a recessed
 *     well. Used for view / scope toggles ("open / resolved / all",
 *     "all / mine / unassigned"). The active option gets a raised tile.
 *
 *   variant="underline" — text labels with a brand-tinted underline on
 *     the active item. Used for in-page tabs (settings sections,
 *     project sub-routes). Higher visual hierarchy.
 *
 * Always keyboard-accessible — the buttons are real <button>s in tab
 * order, not custom role=tab. This keeps `/` and `j/k` row keys from
 * fighting tab activation.
 */
export type SegmentedOption<V extends string> = {
  id: V;
  label: React.ReactNode;
  /** Optional inline count rendered after the label, e.g. "Open 12". */
  count?: number;
  /** Optional disabled flag — dims and blocks the click. */
  disabled?: boolean;
};

export type SegmentedProps<V extends string> = {
  value: V;
  onChange: (next: V) => void;
  options: SegmentedOption<V>[];
  variant?: "segmented" | "underline";
  size?: "sm" | "md";
  className?: string;
  /** Optional aria-label for the role=tablist wrapper. */
  ariaLabel?: string;
};

export function Segmented<V extends string>({
  value,
  onChange,
  options,
  variant = "segmented",
  size = "sm",
  className,
  ariaLabel,
}: SegmentedProps<V>) {
  if (variant === "underline") {
    return (
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={cn("flex items-center gap-1 border-b border-line-subtle", className)}
      >
        {options.map((opt) => {
          const active = opt.id === value;
          return (
            <button
              key={opt.id}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={opt.disabled}
              onClick={() => onChange(opt.id)}
              className={cn(
                "relative h-8 px-2.5 text-[12.5px] inline-flex items-center gap-1.5 transition focus-ring",
                active ? "text-fg" : "text-fg-muted hover:text-fg-dim",
                opt.disabled && "opacity-50 cursor-not-allowed",
              )}
            >
              <span>{opt.label}</span>
              {typeof opt.count === "number" && (
                <span className="tabular-nums text-[11px] text-fg-muted">{opt.count}</span>
              )}
              {active && (
                <span
                  aria-hidden
                  className="absolute -bottom-px left-0 right-0 h-0.5 bg-brand-500 rounded-full"
                />
              )}
            </button>
          );
        })}
      </div>
    );
  }

  // segmented (default)
  const heightCls = size === "sm" ? "h-7 px-2.5 text-[12px]" : "h-8 px-3 text-[12.5px]";
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex items-center gap-0.5 rounded-md bg-bg-base border border-line-subtle p-0.5",
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <button
            key={opt.id}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={opt.disabled}
            onClick={() => onChange(opt.id)}
            className={cn(
              "rounded transition inline-flex items-center gap-1.5 focus-ring",
              heightCls,
              active
                ? "bg-bg-elev text-fg font-medium"
                : "text-fg-muted hover:text-fg",
              opt.disabled && "opacity-50 cursor-not-allowed",
            )}
          >
            <span>{opt.label}</span>
            {typeof opt.count === "number" && (
              <span className={cn("tabular-nums text-[11px]", active ? "text-fg-muted" : "text-fg-muted")}>
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
